import React, { useState } from 'react';
import { 
  TrendingUp, 
  ShieldAlert, 
  Award, 
  Globe, 
  Cpu, 
  Layers, 
  DollarSign, 
  Zap, 
  FileText,
  Activity,
  Briefcase,
  Smartphone,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const SlideTab = ({ label, index, activeIndex, onClick }) => (
  <button
    onClick={() => onClick(index)}
    className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${
      activeIndex === index
        ? 'bg-[#8B1A2D] text-white shadow-md'
        : 'text-zinc-400 hover:text-white hover:bg-zinc-800 bg-zinc-900 border border-zinc-800'
    }`}
  >
    {label}
  </button>
);

export default function PitchDeckPage() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const rate = 130;

  const toggleVal = (usd) => {
    if (currency === 'KES') {
      return `KES ${(usd * rate).toLocaleString()}`;
    }
    return `$${usd.toLocaleString()}`;
  };

  const slides = [
    // Slide 1: Introduction
    {
      tab: '01. The Vision',
      content: (
        <div className="space-y-6">
          <div>
            <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Introduction</span>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">Predictive Academic &amp; Logistics Curation</h2>
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-3xl">
            The DIS Nexus bridges the gap between raw data collection and actionable human context. It is designed to act as the primary operational hub, evaluating the real-time velocity of student growth, training center capacities, and corporate employment pipelines.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
              <h3 className="font-black text-brand-gold uppercase text-xs mb-2">Real-Time Forecasts</h3>
              <p className="text-zinc-300 text-xs leading-relaxed">Calculates student progress vectors to project graduation outcomes three to six months in advance.</p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
              <h3 className="font-black text-brand-gold uppercase text-xs mb-2">Qualitative Overlays</h3>
              <p className="text-zinc-300 text-xs leading-relaxed">Filters raw statistical records through real-world contextual inputs like infrastructure status and travel parameters.</p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
              <h3 className="font-black text-brand-gold uppercase text-xs mb-2">B2B Curation</h3>
              <p className="text-zinc-300 text-xs leading-relaxed">Constructs highly-valuable, verifiable talent records that external industry partners can rely on for rapid placements.</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 2: Pipeline Funnel
    {
      tab: '02. Pipeline Funnel',
      content: (
        <div className="space-y-6">
          <div>
            <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">The Pipeline</span>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">The Student Lifecycle Funnel</h2>
          </div>
          <div className="bg-zinc-950 rounded-2xl p-6 overflow-x-auto border border-zinc-800">
            <pre className="font-mono text-zinc-300 text-[10px] leading-relaxed whitespace-pre">
{`QUANTITATIVE STREAM (High-Frequency)       QUALITATIVE STREAM (Event-Driven)
┌──────────────────────────────────┐       ┌─────────────────────────────────┐
│ • Student Daily Reports          │       │ • Human Interview Check-ins     │
│ • Trainer Weekly Grades          │       │ • Socio-Economic surveys        │
│ • System Attendance Telemetry    │       │ • Infrastructure Outage Logs    │
└────────────────┬─────────────────┘       └────────────────┬────────────────┘
                 │                                          │
                 ▼                                          ▼
     [ Cloudflare Workers Edge ]                [ Cloudflare Workers Edge ]
                 │                                          │
                 ▼                                          ▼
     [ Cloudflare D1 SQL DB ]                  [ Firebase Firestore NoSQL ]
(Massive volume, low-cost event log)          (Rich, metadata-driven documents)
                 │                                          │
                 └────────────────────┬─────────────────────┘
                                      │
                                      ▼
                        [ Nexus Analytics Engine ]
                                      │
                                      ▼
                  ┌───────────────────────────────────────┐
                  │  DIS NEXUS ADMINISTRATIVE DASHBOARD   │
                  │ • Forecast Bands (Trend vs. Prob)     │
                  │ • Velocity Indicators (Derivatives)   │
                  │ • Exception Flags & Blocker Alerts    │
                  └───────────────────────────────────────┘`}
            </pre>
          </div>
        </div>
      )
    },
    // Slide 3: Math Modeling
    {
      tab: '03. Math Engine',
      content: (
        <div className="space-y-6">
          <div>
            <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Mathematical Modeling</span>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">First-Order Velocity &amp; Projections</h2>
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-3xl">
            The platform calculates student progress over time as a velocity vector. Rather than drawing static historical records, the dashboard plots dynamic trajectories.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between">
              <div>
                <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-3">Mathematical Framework</h4>
                <div className="font-mono text-xs bg-zinc-900 text-emerald-400 p-4 rounded-xl space-y-2 border border-zinc-800">
                  <span className="text-zinc-500">// Velocity calculation</span><br />
                  Velocity = ΔMilestones / Δt<br /><br />
                  <span className="text-zinc-500">// Blocker adjustment</span><br />
                  AdjustedRisk = RawRisk * (1 - BlockerWeight)
                </div>
              </div>
            </div>
            <div className="space-y-4 justify-center flex flex-col">
              <div className="flex items-start gap-3 bg-zinc-950 border border-zinc-800 p-4 rounded-2xl">
                <TrendingUp className="text-[#8B1A2D] mt-0.5 shrink-0" size={20} />
                <div>
                  <h4 className="text-xs font-black text-white uppercase tracking-wide">Funnel Acceleration</h4>
                  <p className="text-zinc-300 text-xs mt-1">Monitors cohort transition latency rates to flag bottlenecks.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: Qualitative Overrides
    {
      tab: '04. Qualitative Context',
      content: (
        <div className="space-y-6">
          <div>
            <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Human-in-the-Loop</span>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">Qualitative Blocker Tagging</h2>
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-3xl">
            When raw numbers drop, the system triggers qualitative check-ins to tag external real-world variables, preventing algorithmic errors.
          </p>
          <div className="border border-zinc-800 rounded-2xl overflow-hidden shadow-sm bg-zinc-950">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-zinc-900 border-b border-zinc-800">
                  <th className="text-left p-4 font-black uppercase text-brand-gold tracking-wider">Blocker Category</th>
                  <th className="text-left p-4 font-black uppercase text-zinc-400 tracking-wider">Verbatim Blocker Inputs</th>
                  <th className="text-left p-4 font-black uppercase text-zinc-400 tracking-wider">Actionable Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                <tr>
                  <td className="p-4 font-bold text-red-400">Infrastructure</td>
                  <td className="p-4 text-zinc-300 font-medium">Power blackout, cellular tower outage, transit strikes.</td>
                  <td className="p-4 text-zinc-100">Suspends metric penalties, alerts Campus Registry team.</td>
                </tr>
                <tr>
                  <td className="p-4 font-bold text-amber-400">Socio-Economic</td>
                  <td className="p-4 text-zinc-300 font-medium">Lacked transport fare, part-time shifts, standard of living gaps.</td>
                  <td className="p-4 text-zinc-100">Generates automated micro-grant alert to Sponsor Registry.</td>
                </tr>
                <tr>
                  <td className="p-4 font-bold text-blue-400">Personal / Health</td>
                  <td className="p-4 text-zinc-300 font-medium">Medical emergency, illness, direct family constraints.</td>
                  <td className="p-4 text-zinc-100">Adjusts expected graduation target timeline automatically.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    // Slide 5: Business Value
    {
      tab: '05. Business Case',
      content: (
        <div className="space-y-6">
          <div>
            <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Monetization</span>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">Why Businesses Will Buy This Data</h2>
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-3xl">
            This is a highly-verifiable, context-adjusted, real-time index of upcoming talent pools and operational capacities.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div className="space-y-4">
              <div className="flex gap-3 bg-zinc-950 border border-zinc-800 p-4 rounded-2xl">
                <ShieldAlert className="text-[#8B1A2D] shrink-0 mt-0.5" size={20} />
                <p className="text-zinc-300 text-xs leading-relaxed">
                  <strong className="text-white block font-black uppercase text-[10px] tracking-wide mb-1">Adjusted Integrity</strong>
                  Industry recruiters receive scores reflecting true work ethic, bypassing regional power outages or personal emergencies.
                </p>
              </div>
              <div className="flex gap-3 bg-zinc-950 border border-zinc-800 p-4 rounded-2xl">
                <Briefcase className="text-[#8B1A2D] shrink-0 mt-0.5" size={20} />
                <p className="text-zinc-300 text-xs leading-relaxed">
                  <strong className="text-white block font-black uppercase text-[10px] tracking-wide mb-1">Just-in-Time Placement</strong>
                  Organizations can book talent supply lines months before students graduate, matching exact technical skillsets.
                </p>
              </div>
            </div>
            <div className="bg-[#8B1A2D]/5 rounded-2xl p-6 flex flex-col justify-center items-center text-center border border-[#8B1A2D]/10">
              <Award className="text-brand-gold mb-2" size={32} />
              <h4 className="font-black text-white uppercase tracking-wider text-xs">Verifiable Talent Ledger</h4>
              <p className="text-zinc-300 text-[10px] mt-2 max-w-xs font-semibold">Bypasses traditional recruitment screening delays through authenticated performance histories.</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 6: API Monetization
    {
      tab: '06. API Pipelines',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Ecosystem Revenue</span>
              <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">API Revenue Pipelines</h2>
            </div>
            <div className="bg-[#8B1A2D]/10 border border-[#8B1A2D]/20 rounded-xl px-4 py-2 text-right">
              <span className="text-[8px] font-black uppercase tracking-wider text-brand-gold">Projected Estimate</span>
              <p className="text-lg font-black text-white">{toggleVal(17200)} / mo</p>
            </div>
          </div>
          <div className="border border-zinc-800 rounded-2xl overflow-hidden shadow-sm max-h-[300px] overflow-y-auto bg-zinc-950">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-zinc-900 border-b border-zinc-800 sticky top-0">
                  <th className="text-left p-3 font-black uppercase text-brand-gold tracking-wider">Target Partner</th>
                  <th className="text-left p-3 font-black uppercase text-zinc-400 tracking-wider">API Telemetry Value Stream</th>
                  <th className="text-right p-3 font-black uppercase text-zinc-400 tracking-wider">Est. Monthly</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                <tr>
                  <td className="p-3 font-bold text-white">Banking / Equity Group</td>
                  <td className="p-3 text-zinc-300 font-medium">USSD + SIM transaction rates and risk indices for credit scoring policies.</td>
                  <td className="p-3 text-right font-bold text-white">{toggleVal(2500)}</td>
                </tr>
                <tr>
                  <td className="p-3 font-bold text-white">Power &amp; Utility Companies</td>
                  <td className="p-3 text-zinc-300 font-medium">Localized grid outage telemetry mapped to planning zones.</td>
                  <td className="p-3 text-right font-bold text-white">{toggleVal(1800)}</td>
                </tr>
                <tr>
                  <td className="p-3 font-bold text-white">Solar / Green Energy</td>
                  <td className="p-3 text-zinc-300 font-medium">Regional energy deficit tracking for mini-grid optimization layouts.</td>
                  <td className="p-3 text-right font-bold text-white">{toggleVal(1500)}</td>
                </tr>
                <tr>
                  <td className="p-3 font-bold text-white">Internet &amp; Telco Providers</td>
                  <td className="p-3 text-zinc-300 font-medium">Outage mapping by area to justify local tower upgrades.</td>
                  <td className="p-3 text-right font-bold text-white">{toggleVal(1600)}</td>
                </tr>
                <tr>
                  <td className="p-3 font-bold text-white">Corporate HR Divisions</td>
                  <td className="p-3 text-zinc-300 font-medium">Talent supply forecasting metrics for direct recruiting pipelines.</td>
                  <td className="p-3 text-right font-bold text-white">{toggleVal(2200)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    // Slide 7: In-House Private Server
    {
      tab: '07. In-House Server',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Option A</span>
              <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">Local Private Server (100k scale)</h2>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-right">
              <span className="text-[8px] font-black uppercase tracking-wider text-brand-gold">Budget Estimate</span>
              <p className="text-lg font-black text-white">{toggleVal(3000)} Setup</p>
            </div>
          </div>
          <div className="border border-zinc-800 rounded-2xl overflow-hidden shadow-sm bg-zinc-950">
            <table className="w-full text-xs text-zinc-300">
              <thead>
                <tr className="bg-zinc-900 border-b border-zinc-800 text-brand-gold">
                  <th className="text-left p-3 font-black uppercase tracking-wider">Item Description</th>
                  <th className="text-right p-3 font-black uppercase tracking-wider">CapEx</th>
                  <th className="text-right p-3 font-black uppercase tracking-wider">OpEx</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                <tr>
                  <td className="p-3 text-white font-bold">AI &amp; Compute Node (Custom-Built, High VRAM GPU)</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(1500)}</td>
                  <td className="p-3 text-right text-zinc-500">—</td>
                </tr>
                <tr>
                  <td className="p-3 text-white font-bold">Redundant NAS Array Storage</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(600)}</td>
                  <td className="p-3 text-right text-zinc-500">—</td>
                </tr>
                <tr>
                  <td className="p-3 text-white font-bold">Power Backup Unit (UPS for Outages)</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(400)}</td>
                  <td className="p-3 text-right text-zinc-500">—</td>
                </tr>
                <tr>
                  <td className="p-3 text-white font-bold">Monthly Maintenance &amp; Fiber Connection</td>
                  <td className="p-3 text-right text-zinc-500">—</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(150)} / mo</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    // Slide 8: Managed Cloud
    {
      tab: '08. Managed Cloud',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-[10px] font-black text-brand-gold uppercase tracking-[0.25em]">Option B</span>
              <h2 className="text-3xl font-black text-white uppercase tracking-tight mt-1">Managed Cloud Cost Model</h2>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-right">
              <span className="text-[8px] font-black uppercase tracking-wider text-brand-gold">Budget Estimate</span>
              <p className="text-lg font-black text-white">{toggleVal(510)} / mo</p>
            </div>
          </div>
          <div className="border border-zinc-800 rounded-2xl overflow-hidden shadow-sm bg-zinc-950">
            <table className="w-full text-xs text-zinc-300">
              <thead>
                <tr className="bg-zinc-900 border-b border-zinc-800 text-brand-gold">
                  <th className="text-left p-3 font-black uppercase tracking-wider">Item Description</th>
                  <th className="text-right p-3 font-black uppercase tracking-wider">Monthly OpEx</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                <tr>
                  <td className="p-3 text-white font-bold">Cloud Run / Azure Functions (Compute Engine Execution)</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(80)}</td>
                </tr>
                <tr>
                  <td className="p-3 text-white font-bold">Cloud Firestore NoSQL (Qualitative Database Storage)</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(120)}</td>
                </tr>
                <tr>
                  <td className="p-3 text-white font-bold">Cloudflare D1 (Edge Telemetry Logs)</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(20)}</td>
                </tr>
                <tr>
                  <td className="p-3 text-white font-bold">AI Analytical Mapping API quota limits</td>
                  <td className="p-3 text-right text-white font-semibold">{toggleVal(240)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-center border-b border-zinc-800 pb-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight">Nexus Pitch Deck</h1>
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mt-1">
            Ecosystem Slideshow &amp; Commercial Feasibility Models
          </p>
        </div>

        {/* Currency Switcher */}
        <div className="flex bg-zinc-900 rounded-xl p-1 border border-zinc-800">
          <button
            onClick={() => setCurrency('USD')}
            className={`px-3 py-1.5 text-[10px] font-black rounded-lg transition-all ${
              currency === 'USD' ? 'bg-[#8B1A2D] text-white shadow-sm' : 'text-zinc-400'
            }`}
          >
            USD
          </button>
          <button
            onClick={() => setCurrency('KES')}
            className={`px-3 py-1.5 text-[10px] font-black rounded-lg transition-all ${
              currency === 'KES' ? 'bg-[#8B1A2D] text-white shadow-sm' : 'text-zinc-400'
            }`}
          >
            KES
          </button>
        </div>
      </div>

      {/* Tabs / Slide Picker */}
      <div className="flex flex-wrap gap-2">
        {slides.map((s, idx) => (
          <SlideTab
            key={idx}
            label={s.tab}
            index={idx}
            activeIndex={activeSlide}
            onClick={setActiveSlide}
          />
        ))}
      </div>

      {/* Slide Container */}
      <div className="bg-zinc-900 border border-zinc-850 rounded-3xl p-8 shadow-xl min-h-[400px] flex flex-col justify-center">
        {slides[activeSlide].content}
      </div>

      {/* Navigation Controls */}
      <div className="flex justify-between items-center text-[10px] text-zinc-400 font-bold uppercase tracking-widest bg-zinc-900 border border-zinc-800 rounded-2xl px-6 py-4">
        <span>Click tabs above to switch slides</span>
        <div className="flex gap-4">
          <button
            disabled={activeSlide === 0}
            onClick={() => setActiveSlide(prev => Math.max(0, prev - 1))}
            className="hover:text-white disabled:opacity-30 flex items-center gap-1"
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <button
            disabled={activeSlide === slides.length - 1}
            onClick={() => setActiveSlide(prev => Math.min(slides.length - 1, prev + 1))}
            className="hover:text-white disabled:opacity-30 flex items-center gap-1"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
